// RITA-1498 (Nutzungserfassung Phase 1b) — Apollo-Link misst Serveraufrufe (Konzept 3.5, 4.1).
// Steht in components/ErrorMessage/ErrorMessage.tsx VOR dem Upload-Link. Meldet je Operation
// genau ein api-Ereignis: Name, Art, Dauer, Erfolg bzw. erster Fehlercode. Nie Variablen,
// nie Antwortdaten. Fehler werden unverändert weitergereicht.
import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import type { DocumentNode } from 'graphql';
import { UNKNOWN_NAME } from './apiName';
import type { UsageEventInput } from './tracker';

const MAX_NAME_LENGTH = 128;
const MAX_CODE_LENGTH = 32;

const describeOperation = (
  query: DocumentNode,
  operationName?: string,
): { name: string; kind: 'query' | 'mutation' } => {
  const definition = getMainDefinition(query);
  const isOperation = definition.kind === 'OperationDefinition';
  const kind = isOperation && definition.operation === 'mutation' ? 'mutation' : 'query';
  let name = operationName || '';
  if (!name && isOperation) {
    const first = definition.selectionSet.selections[0];
    if (first && first.kind === 'Field') name = first.name.value;
  }
  return { name: (name || UNKNOWN_NAME).slice(0, MAX_NAME_LENGTH), kind };
};

const errorCode = (error: unknown): string => {
  const candidate = error as { statusCode?: unknown; status?: unknown; name?: unknown } | null;
  const code = candidate?.statusCode ?? candidate?.status ?? candidate?.name ?? 'network';
  return String(code).slice(0, MAX_CODE_LENGTH);
};

export const createUsageApolloLink = (
  record: (input: UsageEventInput) => void,
  now: () => number = () => Date.now(),
): ApolloLink =>
  new ApolloLink((operation, forward) => {
    const { name, kind } = describeOperation(operation.query, operation.operationName);
    const startedAt = now();
    let reported = false;

    const report = (ok: boolean, code?: string): void => {
      if (reported) return;
      reported = true;
      const props: UsageEventInput['props'] = { kind, ok };
      if (code) props.code = code;
      try {
        record({ type: 'api', name, value: Math.max(0, now() - startedAt), props });
      } catch {
        // Die Erfassung darf den Aufruf nie stören.
      }
    };

    return new Observable<FetchResult>((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          const firstError = result.errors && result.errors[0];
          if (firstError) {
            report(false, String(firstError.extensions?.code ?? 'GRAPHQL_ERROR').slice(0, MAX_CODE_LENGTH));
          } else {
            report(true);
          }
          observer.next(result);
        },
        error: (error) => {
          report(false, errorCode(error));
          observer.error(error);
        },
        complete: () => {
          report(true);
          observer.complete();
        },
      });
      return () => subscription.unsubscribe();
    });
  });

export default createUsageApolloLink;
